import type { EventHook } from '@vueuse/core';
import type { ComputedRef, Ref } from 'vue';
import type { DetailPageContext } from '#layers/block-engine';
import type { AnyItem, GlobalCollectionId } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { RStoreFormObject } from '~/composables/use-form-submit-handler';
import type { FormBlockSuccessEventPayload, FormSaveContext, FormSaveError } from './types';
import { ref } from 'vue';
import { useRouter } from '#app/composables/router';
import { getItemPrimaryKey } from '#layers/data-model/utils/primary-key';
import { useCollectionRoutes } from '~/composables/use-collection-routes';
import { useFormSubmitHandler } from '~/composables/use-form-submit-handler';

export interface UseFormBlockSubmitParams {
	formObject: Ref<RStoreFormObject<AnyItem> | null>;
	isCreate: ComputedRef<boolean>;
	finalCollectionId: ComputedRef<GlobalCollectionId | undefined>;
	collectionData: ComputedRef<ResolvedMonospaceCollection | undefined>;
	isDetailPageForm: ComputedRef<boolean>;
	detailPageContext: Pick<DetailPageContext, 'formMode'>;
	saveHook: EventHook<FormSaveContext>;
	saveHandlerErrors: Ref<FormSaveError[]>;
	clearSaveHandlerErrors: () => void;
	emitOnRootBlock: (event: string, payload: FormBlockSuccessEventPayload) => void;
	onSuccess?: (payload: FormBlockSuccessEventPayload) => void;
	onSubmit?: (data: { data: AnyItem }) => void;
	hasExternalChanges: () => boolean;
}

export interface UseFormBlockSubmitReturn {
	onSubmit: () => Promise<void>;
	/**
	 * Form-only submit disabled state.
	 * Does NOT consider external changes - the master composable handles that.
	 * Returns true when form has no changes (in update mode).
	 */
	formOnlySubmitDisabled: ComputedRef<boolean>;
	submitError: Ref<Error | null>;
	submitted: Ref<boolean>;
}

/**
 * Handles form submission logic for form blocks.
 * Supports both submit mode (persists to backend) and emit mode (just emits data).
 */
export function useFormBlockSubmit(params: UseFormBlockSubmitParams): UseFormBlockSubmitReturn {
	const {
		formObject,
		isCreate,
		finalCollectionId,
		collectionData,
		isDetailPageForm,
		detailPageContext,
		saveHook,
		saveHandlerErrors,
		clearSaveHandlerErrors,
		emitOnRootBlock,
		onSuccess,
		onSubmit: onSubmitCallback,
		hasExternalChanges,
	} = params;

	const submitted = ref(false);

	const router = useRouter();
	const { detailRoute } = useCollectionRoutes(collectionData);

	const { onSubmit: handleSubmit, submitDisabled: formOnlySubmitDisabled, submitError } = useFormSubmitHandler<AnyItem>(formObject, {
		isCreate,
		onBeforeSubmit: () => {
			submitted.value = false;
			clearSaveHandlerErrors();
		},
		onSuccess: async (result) => {
			if (!collectionData.value) {
				throw new Error('Collection data unavailable during form success handling');
			}

			const itemKey = getItemPrimaryKey(result, collectionData.value);

			// Trigger save handlers (they run in parallel via createEventHook)
			const handlerResults = await saveHook.trigger({ itemKey });

			// Collect all errors from handlers
			saveHandlerErrors.value = handlerResults
				.flat()
				.filter((error): error is FormSaveError => !!error);

			submitted.value = true;

			const successPayload: FormBlockSuccessEventPayload = {
				key: itemKey,
				data: result,
			};

			onSuccess?.(successPayload);

			if (isDetailPageForm.value) {
				emitOnRootBlock('success', successPayload);
			}

			// Redirect to the detail page to edit the item.
			// Gated on `isCreate` (route item id), not page intent: create and edit now share the
			// same 'edit' page, so `pageIntent` would be 'edit' even when creating.
			if (isDetailPageForm.value && isCreate.value && collectionData.value) {
				const route = detailRoute(itemKey);

				if (route) {
					router.push(route);
				}
			}
		},
	});

	async function triggerSaveHandlersOnly() {
		if (!formObject.value || !collectionData.value) return;

		submitted.value = false;
		clearSaveHandlerErrors();

		const itemKey = getItemPrimaryKey(formObject.value, collectionData.value);
		const handlerResults = await saveHook.trigger({ itemKey });

		saveHandlerErrors.value = handlerResults
			.flat()
			.filter((error): error is FormSaveError => !!error);

		submitted.value = true;

		const successPayload: FormBlockSuccessEventPayload = {
			key: itemKey,
			data: formObject.value?.$getRawData({ clone: true }) ?? {},
		};

		onSuccess?.(successPayload);

		if (isDetailPageForm.value) {
			emitOnRootBlock('success', successPayload);
		}
	}

	async function onSubmit() {
		// Emit submit callback before mode-specific logic
		if (onSubmitCallback) {
			onSubmitCallback({ data: formObject.value?.$getRawData({ clone: true }) ?? {} });
		}

		if (!finalCollectionId.value) {
			submitted.value = false;
			clearSaveHandlerErrors();
			submitted.value = true;
			submitError.value = null;
			return;
		}

		if (!detailPageContext.formMode || detailPageContext.formMode === 'submit') {
			if (!formOnlySubmitDisabled.value) {
				await handleSubmit();
			}
			else if (hasExternalChanges()) {
				// Form has no field changes, but external save handlers (e.g. policy assignment) may need to persist
				await triggerSaveHandlersOnly();
			}
		}
		else {
			// Non-submit mode (emit mode) - just emit success without persisting
			// Reset state before submission (matches submit mode behavior)
			submitted.value = false;
			clearSaveHandlerErrors();

			try {
				if (!collectionData.value) {
					throw new Error('Collection data unavailable during form emit handling');
				}

				const clonedData = formObject.value?.$getRawData({ clone: true }) ?? {};

				const payload: FormBlockSuccessEventPayload = {
					key: getItemPrimaryKey(clonedData, collectionData.value),
					data: clonedData,
				};

				submitError.value = null;
				submitted.value = true;

				onSuccess?.(payload);

				if (isDetailPageForm.value) {
					emitOnRootBlock('success', payload);
				}
			}
			catch (error) {
				submitError.value = error instanceof Error ? error : new Error(String(error));
			}
		}
	}

	return {
		onSubmit,
		formOnlySubmitDisabled,
		submitError,
		submitted,
	};
}
